"use client"

import { useState, useEffect, useRef } from "react"
import { Modal } from "@/components/ui/modal"
import { cn } from "@/lib/utils"
import { User, X, UserX, Loader2, Users, CreditCard } from "lucide-react"
import { useAppSelector, useAppDispatch } from "@/app/store/hooks"
import { getInitials } from "@/lib/utils"
import { BasicInfoTab } from "./basic-info-tab"
import { ReferralTab } from "./referral-tab"
import { TransactionsTab } from "./transactions-tab"
import { useDeleteAccountMutation } from "@/app/store/api/authApi"
import { logout } from "@/app/store/slices/authSlice"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import Link from "next/link"

interface SettingsModalProps {
    isOpen: boolean
    onClose: () => void
    initialTab?: Tab
}

export type Tab = "basic" | "referral" | "billing"

export function SettingsModal({ isOpen, onClose, initialTab = "basic" }: SettingsModalProps) {
    const dispatch = useAppDispatch()
    const [activeTab, setActiveTab] = useState<Tab>(initialTab)
    const { user } = useAppSelector((state) => state.auth)
    const [deleteAccount, { isLoading: isDeleting }] = useDeleteAccountMutation()
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
    const initials = getInitials(user?.firstName, user?.lastName)
    const basicInfoRef = useRef<any>(null)
    const [isSaving, setIsSaving] = useState(false)

    // Reset tab when modal opens or initialTab changes
    useEffect(() => {
        if (isOpen) {
            setActiveTab(initialTab)
        }
    }, [isOpen, initialTab])

    const handleDeleteAccount = async () => {
        try {
            await deleteAccount({}).unwrap()
            toast.success("Account deleted successfully")
            dispatch(logout())
            onClose()
        } catch (error: any) {
            console.error("Delete account failed:", error)
            toast.error(error?.data?.message || "Failed to delete account")
        } finally {
            setIsDeleteDialogOpen(false)
        }
    }

    const handleSaveAndClose = async () => {
        setIsSaving(true)
        try {
            if (activeTab === "basic" && basicInfoRef.current) {
                const success = await basicInfoRef.current.handleSave()
                if (success) {
                    onClose()
                }
            } else {
                onClose()
            }
        } catch (error) {
            console.error("Save failed:", error)
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} className="max-w-[800px] w-full p-0 overflow-hidden rounded-2xl h-[85vh] md:h-[650px] flex flex-col md:flex-row shadow-2xl bg-white">
                {/* Left Sidebar */}
                <div className="w-full md:w-[200px] bg-[#f9fafc] flex flex-col border-b md:border-b-0 md:border-r border-slate-200">
                    <div className="px-4 flex flex-col gap-1 overflow-x-auto md:overflow-visible" style={{ scrollbarWidth: "none" }}>
                        <div className="flex flex-col w-full pt-4 mb-5 px-2">
                            <div className="flex items-center gap-2 font-bold text-lg leading-none">
                                <Link href="/">Deckwale</Link>
                                
                            </div>
                            <span className="text-[10px] font-semibold text-blue-600 leading-none mt-1">by Seedicon</span>
                        </div>
                        <TabButton
                            active={activeTab === "basic"}
                            onClick={() => setActiveTab("basic")}
                            label="Profile"
                            icon={User}
                        />
                        <TabButton
                            active={activeTab === "referral"}
                            onClick={() => setActiveTab("referral")}
                            label="Referral"
                            icon={Users}
                        />
                        <TabButton
                            active={activeTab === "billing"}
                            onClick={() => setActiveTab("billing")}
                            label="Plans & Billing"
                            icon={CreditCard}
                        />
                    </div>
                </div>

                {/* Right Content Area */}
                <div className="flex-1 flex flex-col relative bg-white min-w-0">


                    <div className="px-8 py-3 border-b border-slate-100 flex-shrink-0">
                        <h3 className="text-xl font-semibold text-slate-900">
                            {activeTab === "basic" && "Profile"}
                            {activeTab === "referral" && "Referral"}
                            {activeTab === "billing" && "Plans & Billing"}
                        </h3>
                    </div>

                    <div className="flex-1 overflow-y-auto px-8 py-6">
                        {activeTab === "basic" && (
                            <BasicInfoTab
                                ref={basicInfoRef}
                                user={user}
                                initials={initials}
                                onDeleteAccount={() => setIsDeleteDialogOpen(true)}
                            />
                        )}
                        {activeTab === "referral" && <ReferralTab />}
                        {activeTab === "billing" && <TransactionsTab />}
                    </div>

                    <div className="px-8 py-4 border-t border-slate-100 bg-white flex justify-end flex-shrink-0">
                        <button
                            onClick={handleSaveAndClose}
                            disabled={isSaving}
                            className="bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 rounded-xl text-[14px] font-medium transition-colors shadow-sm flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                            Done
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={isDeleteDialogOpen}
                onClose={() => !isDeleting && setIsDeleteDialogOpen(false)}
                className="max-w-md p-6"
            >
                <div className="space-y-4">
                    <div className="flex items-center gap-3 text-red-600 mb-2">
                        <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                            <UserX className="h-5 w-5" />
                        </div>
                        <h3 className="text-xl font-bold">Delete Account</h3>
                    </div>

                    <div className="space-y-3">
                        <p className="text-sm font-medium text-slate-900">
                            Are you sure you want to delete your account?
                        </p>
                        <p className="text-sm text-slate-500 leading-relaxed">
                            This action is <strong>permanent</strong> and cannot be undone. All your projects, assets, and credits will be permanently deleted from our servers.
                        </p>
                    </div>

                    <div className="flex justify-end gap-3 pt-2">
                        <Button
                            variant="ghost"
                            onClick={() => setIsDeleteDialogOpen(false)}
                            disabled={isDeleting}
                            className="hover:bg-slate-100"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDeleteAccount}
                            disabled={isDeleting}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {isDeleting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Deleting...
                                </>
                            ) : (
                                "Delete My Account"
                            )}
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    )
}

function TabButton({ active, label, icon: Icon, onClick, disabled }: { active: boolean, label: string, icon: any, onClick: () => void, disabled?: boolean }) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={cn(
                "flex cursor-pointer items-center gap-2 w-full px-2 py-1.5 text-[13px] font-medium rounded-md transition-colors text-left whitespace-nowrap flex-shrink-0 md:flex-shrink",
                active ? "bg-slate-200/70 text-slate-900" : "text-slate-600 hover:bg-slate-100",
                disabled && "opacity-50 cursor-not-allowed hover:bg-transparent hover:text-slate-600"
            )}
        >
            <Icon className="w-[15px] h-[15px] stroke-[2]" />
            {label}
        </button>
    )
}
